
import { useState } from "react";
import { LeadAccount, StrategyProvider } from "@/types/strategy-types";

interface UseLeadAccountsTableProps {
  leadAccounts: LeadAccount[];
  providers: StrategyProvider[];
  onCreateLeadAccount: (leadAccount: Omit<LeadAccount, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  onUpdateLeadAccount: (id: string, leadAccount: Partial<LeadAccount>) => Promise<void>;
  onDeleteLeadAccount: (id: string) => Promise<void>;
}

export const useLeadAccountsTable = ({
  leadAccounts,
  providers,
  onCreateLeadAccount,
  onUpdateLeadAccount,
  onDeleteLeadAccount
}: UseLeadAccountsTableProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLeadAccount, setSelectedLeadAccount] = useState<LeadAccount | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string>("all");

  // Filter lead accounts based on search term and provider filter
  const filteredLeadAccounts = leadAccounts.filter(account => {
    // Search in external_id, exchange, and provider name
    const matchesSearch = 
      account.external_id.toLowerCase().includes(searchTerm.toLowerCase()) || 
      account.exchange.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (account.provider_name && account.provider_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesProvider = selectedProvider === "all" || account.strategy_provider_id === selectedProvider;
    
    return matchesSearch && matchesProvider;
  });

  const handleEdit = (leadAccount: LeadAccount) => {
    setSelectedLeadAccount(leadAccount);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (leadAccount: LeadAccount) => {
    setSelectedLeadAccount(leadAccount);
    setIsDeleteDialogOpen(true);
  };

  const handleCreateLeadAccount = async (data: Omit<LeadAccount, 'id' | 'created_at' | 'updated_at'>) => {
    await onCreateLeadAccount(data);
    setIsCreateDialogOpen(false);
  };

  const handleUpdateLeadAccount = async (id: string, data: Partial<LeadAccount>) => {
    await onUpdateLeadAccount(id, data);
    setIsEditDialogOpen(false);
  };

  const handleDeleteLeadAccount = async (id: string) => {
    await onDeleteLeadAccount(id);
    setIsDeleteDialogOpen(false);
  };

  const openCreateDialog = () => setIsCreateDialogOpen(true);
  const closeCreateDialog = () => setIsCreateDialogOpen(false);
  const closeEditDialog = () => setIsEditDialogOpen(false);
  const closeDeleteDialog = () => setIsDeleteDialogOpen(false);

  return {
    searchTerm,
    setSearchTerm,
    selectedProvider,
    setSelectedProvider,
    filteredLeadAccounts,
    selectedLeadAccount,
    isCreateDialogOpen,
    isEditDialogOpen,
    isDeleteDialogOpen,
    handleEdit,
    handleDelete,
    handleCreateLeadAccount,
    handleUpdateLeadAccount,
    handleDeleteLeadAccount,
    openCreateDialog,
    closeCreateDialog,
    closeEditDialog,
    closeDeleteDialog
  };
};
